/* import __COLOCATED_TEMPLATE__ from './bulk-number-porting.hbs'; */
/* RESPONSIBLE TEAM: team-phone */

import type AppService from 'embercom/services/app-service';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { type Task } from 'ember-concurrency';
import { post } from 'embercom/lib/ajax';
import { type PhoneNumber } from 'embercom/models/settings/calling';
import type IntlService from 'embercom/services/intl';
import type IntercomCallService from 'embercom/services/intercom-call-service';
import type Store from '@ember-data/store';

export enum PortingRequestSteps {
  Disclaimer = 'disclaimer',
  NumberInputForm = 'number-input-form',
  RegulatoryChecker = 'regulatory-checker',
  NumberConfirmation = 'number-confirmation',
  NextSteps = 'next-steps',
}

interface Args {
  activateProvisioningModal: () => any;
  peekPhoneNumbersTask: Task<Array<PhoneNumber>, any>;
  isNumberProvisioningPrimary: boolean;
}

interface Signature {
  Args: Args;
}

export default class BulkNumberPorting extends Component<Signature> {
  @tracked showBulkPortingModal = false;
  @tracked currentStep: PortingRequestSteps = PortingRequestSteps.Disclaimer;
  @tracked phoneNumbersInput = '';
  @tracked phoneNumberDetails: any[] = [];
  @tracked unsupportedNumbers: any[] = [];
  @tracked regulatoryRequirements: any[] = [];
  @tracked groupedNumberSummary: { country: string; count: number }[] = [];
  @service declare intercomCallService: IntercomCallService;
  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare notificationsService: any;
  @service declare store: Store;

  @action openBulkPortingModal() {
    this.showBulkPortingModal = true;
  }

  @action closeBulkPortingModal() {
    this.showBulkPortingModal = false;
    this.currentStep = PortingRequestSteps.Disclaimer;
    this.resetForm();
  }

  @action resetForm() {
    this.phoneNumbersInput = '';
  }

  @action continueToNumberInputForm() {
    this.currentStep = PortingRequestSteps.NumberInputForm;
  }

  @action async continueToNumberConfirmation() {
    let response = await post(`/ember/calling_phone_numbers/bulk_number_lookup`, {
      phone_numbers: this.phoneNumbersInput.trim(),
      app_id: this.appService.app.id,
    });
    this.phoneNumberDetails = response.phone_numbers;
    this.unsupportedNumbers = response.unsupported_numbers;
    this.regulatoryRequirements = response.regulatory_information;
    this.currentStep = PortingRequestSteps.NumberConfirmation;
  }

  get hasValidNumberInput() {
    return !!this.phoneNumbersInput?.trim();
  }

  get canCreateBulkPort(): boolean {
    return this.phoneNumberDetails && this.phoneNumberDetails.length > 0;
  }

  @action async createBulkPort() {
    try {
      let groupedNumberData = this.phoneNumberDetails.reduce(
        (acc: Record<string, any>, number: any) => {
          let countryCode = number.country_code;
          if (!acc[countryCode]) {
            acc[countryCode] = {
              phone_numbers: [],
              regulatory_requirements: this.regulatoryRequirements.find(
                (req: any) => req.country_code === countryCode,
              ),
            };
          }
          acc[countryCode].phone_numbers.push(number);
          return acc;
        },
        {},
      );

      this.groupedNumberSummary = Object.keys(groupedNumberData).map((countryCode) => ({
        country: countryCode,
        count: groupedNumberData[countryCode].phone_numbers.length,
      }));

      await post(`/ember/calling_phone_numbers/create_bulk_port_request_per_country`, {
        app_id: this.appService.app.id,
        country_data: groupedNumberData,
      });

      await this.store.findAll('calling-phone-number', { reload: true });
      this.currentStep = PortingRequestSteps.NextSteps;
      this.args.peekPhoneNumbersTask.perform();
    } catch (error) {
      this.notificationsService.notifyError(
        this.intl.t('calling.settings.phone-calls.porting.bulk.error.create-bulk-port-error'),
      );
    }
  }

  @action closePortingModal() {
    this.showBulkPortingModal = false;
    this.currentStep = PortingRequestSteps.Disclaimer;
    this.resetForm();
  }
}
declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Calling::Settings::BulkNumberPorting': typeof BulkNumberPorting;
    'calling/settings/bulk-number-porting': typeof BulkNumberPorting;
  }
}
